import React, { useEffect, useRef, useState } from 'react';
import { useScrollable } from './ScrollableProvider';
import './Scrollable.scss';

export const ScrollableViewport = ({ children, className = '' }) => {
  const {
    contentRef,
    viewportW,
    viewportH,
    horizontal,
    vertical,
  } = useScrollable();

  return (
    <div
      ref={contentRef}
      className={`scrollable-viewport ${className}${vertical ? ' vertical' : ''} ${horizontal ? ' horizontal' : ''}`}
      style={{
        width: viewportW ? viewportW : '100%',
        height: viewportH || undefined,
        overflowX: horizontal ? 'auto' : 'hidden',
        overflowY: vertical ? 'auto' : 'hidden',
        scrollBehavior: 'smooth',
      }}
    >
      {children}
    </div>
  );
};

export const Scrollbar = ({
  orientation = 'horizontal',
  className = '',
}) => {
  const {
    contentRef,
    horizontal,
    vertical,
    isHorizontalOverflowing,
    isVerticalOverflowing,
  } = useScrollable();

  const trackRef = useRef(null);
  const [thumbSize, setThumbSize] = useState(0);
  const [thumbOffset, setThumbOffset] = useState(0);

  const shouldRender =
    orientation === 'horizontal'
      ? horizontal && isHorizontalOverflowing
      : vertical && isVerticalOverflowing;

  useEffect(() => {
    if (!shouldRender) return;

    const update = () => {
      const el = contentRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      if (orientation === 'horizontal') {
        const contentWidth = el.clientWidth;
        const scrollWidth = el.scrollWidth;
        const trackWidth = track.clientWidth;
        const rawRatio =
          scrollWidth > 0 ? contentWidth / scrollWidth : 1;
        const size = Math.max(rawRatio * trackWidth, 30);
        const maxOffset = trackWidth - size;
        const maxScroll = scrollWidth - contentWidth;

        let scrollRatio = 0;
        if (maxScroll > 0) {
          scrollRatio = el.scrollLeft / maxScroll;
          scrollRatio = Math.min(1, Math.max(0, scrollRatio));
        }

        setThumbSize(size);
        setThumbOffset(scrollRatio * maxOffset);
      }

      if (orientation === 'vertical') {
        const contentHeight = el.clientHeight;
        const scrollHeight = el.scrollHeight;
        const trackHeight = track.clientHeight;
        const rawRatio =
          scrollHeight > 0 ? contentHeight / scrollHeight : 1;
        const size = Math.max(rawRatio * trackHeight, 30);
        const maxOffset = trackHeight - size;
        const maxScroll = scrollHeight - contentHeight;

        let scrollRatio = 0;
        if (maxScroll > 0) {
          scrollRatio = el.scrollTop / maxScroll;
          scrollRatio = Math.min(1, Math.max(0, scrollRatio));
        }
        setThumbSize(size);
        setThumbOffset(scrollRatio * maxOffset);
      }
    };

    const el = contentRef.current;
    if (!el) return;

    update();
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [shouldRender, orientation, contentRef]);

  if (!shouldRender) return null;

  return (
    <div
      ref={trackRef}
      className={`scrollbar-track ${orientation} ${className}`}
      style={{
        position: 'relative',
      }}
    >
      <div
        className="scrollbar-thumb"
        style={{
          ...(orientation === 'horizontal'
            ? {
                height: '100%',
                width: thumbSize,
                transform: `translateX(${thumbOffset}px)`,
              }
            : {
                width: '100%',
                height: thumbSize,
                transform: `translateY(${thumbOffset}px)`,
              }),
        }}
      />
    </div>
  );
};

export const PrevButton = ({ className = '' }) => {
  const { scrollPrev, canScrollPrev } = useScrollable();

  return (
    <button
      type='button'
      className={className}
      onClick={scrollPrev}
      disabled={!canScrollPrev}
      aria-label='Scroll prev'
    >
      <svg className='icon' xmlns="http://www.w3.org/2000/svg" width="6" height="4" viewBox="0 0 6 4" fill="none">
        <path d="M3.5827 0.249571L5.74566 2.41254C6.0091 2.67597 6.06789 2.9774 5.92203 3.31682C5.77617 3.65624 5.51633 3.82623 5.14253 3.82678L0.858196 3.82678C0.483837 3.82678 0.223727 3.65679 0.0778643 3.31736C-0.0679985 2.97794 -0.00921122 2.67651 0.25423 2.41308L2.4172 0.250106C2.57517 0.0922492 2.74082 0.0191651 2.92495 0.0191651C3.10907 0.0191651 3.27431 0.0922492 3.43229 0.250106L3.5827 0.249571Z" fill="currentColor"/>
      </svg>
    </button>
  );
};

export const NextButton = ({ className = '' }) => {
  const { scrollNext, canScrollNext } = useScrollable();

  return (
    <button
      type='button'
      className={className}
      onClick={scrollNext}
      disabled={!canScrollNext}
      aria-label='Scroll next'
    >
      <svg className='icon' xmlns="http://www.w3.org/2000/svg" width="6" height="4" viewBox="0 0 6 4" fill="none">
        <path d="M2.4173 3.57721L0.254335 1.41425C-0.00910259 1.15081 -0.0678908 0.84938 0.0779707 0.50996C0.223832 0.170541 0.483665 0.000554606 0.85747 0L5.1418 0C5.51616 0 5.77627 0.169987 5.92213 0.509407C6.06799 0.848827 6.0092 1.15026 5.74576 1.4137L3.58279 3.57667C3.42482 3.73453 3.25918 3.80762 3.07505 3.80762C2.89093 3.80762 2.72569 3.73453 2.56772 3.57667L2.4173 3.57721Z" fill="currentColor"/>
      </svg>
    </button>
  );
};